import Button from '@/components/Button.jsx';
import HelpIcon from '@/components/HelpIcon.jsx';
import { FiChevronRight, GoTriangleRight } from '@/components/react-icons/main';
import Tag from '@/components/Tag.jsx';
import IconV2 from '@/components/v2/IconV2.jsx';
import confirmDialog from '@/lib/confirmDialog.js';
import { DeleteIcon } from '@/lib/constants/icons.js';

const optionTypeNameMap = {
  pagination: 'Pagination',
  filter: 'Filter',
  cache: 'Cache',
  sortCriteria: 'Sort',
  slices: 'Slice',
  flowControl: 'Flow Control',
  memory: 'Memory',
  logging: 'Logging',
  fields: 'Fields',
  conditionalAuth: 'Conditional Auth',
  connectionEnvironment: 'Connections by Environment',
  condition: 'Condition',
  netsuiteColumnSelector: 'Fields',
  batching: 'Batching',
  graphqlColumnSelector: 'Fields',
  retries: 'Retries',
  parallel: 'Parallel Processing',
  code: 'Code'
};

const PanelFeatureBox = ({ configured, icon, optionType, title, name, description, helpIconContext, tags = [], bullets = [], titleTags = [], actions = [], onClick, onDelete, disabled = false, pointerText = '', className = '', colorTag, tagDataTitle, dataTriggerType }) => {
  const optionTypeName = name || optionTypeNameMap?.[optionType] || optionType;

  const computedTitle = configured ? title : null;
  const computedDescription = configured && !!title ? null : description;

  const handleAction = (e, action) => {
    e?.stopPropagation();
    e?.preventDefault();
    action?.onClick();
  };

  const handleDelete = async () => {
    const confirm = await confirmDialog({
      title: 'Delete Configuration',
      message: 'Are you sure you want to delete this configuration?',
      confirm: 'Delete'
    });

    if (confirm === true) {
      onDelete();
    }
  };

  const displayBullets = bullets.length > 10 ? [...bullets.slice(0, 10), `${bullets.length - 10} more...`] : bullets;

  return (
    <div className={`comp-panel-feature-box ${!configured ? 'unconfigured' : 'configured'} ${disabled ? 'disabled' : ''} option-${optionType} ${className}`} onClick={disabled ? null : onClick} data-trigger-type={dataTriggerType}>
      <div className={'feature-row'}>
        <IconV2 icon={icon} className={'feature-icon'} size={12} />
        <div className={'feature'}>{optionTypeName}</div>
        {helpIconContext && <HelpIcon context={helpIconContext} />}
        {titleTags?.length > 0 && (
          <div className={'tags'}>
            {titleTags.map((tag, index) => (
              <Tag key={index} title={typeof tag === 'string' ? null : tag?.title} {...(tag?.props || {})}>
                {typeof tag === 'string' ? tag : tag?.label}
              </Tag>
            ))}
          </div>
        )}
        {colorTag && <div className={'color-tag'} style={{ backgroundColor: colorTag }} data-title={tagDataTitle} />}
        {actions?.length > 0 && (
          <div className={'actions'}>
            {actions.map((action, index) => (
              <Button key={index} className={`ghost ${action?.className || ''}`} icon={action.icon} title={action.title} size="xs" onClick={(e) => handleAction(e, action)} loading={action.loading} disabled={action.loading} {...(action?.props || {})}>
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      {computedTitle && (
        <div className={'title-row'}>
          <div className={'title'}>{computedTitle}</div>
          {tags?.length > 0 && (
            <div className={'tags'}>
              {tags.map((tag, index) => (
                <Tag key={index} title={typeof tag === 'string' ? null : tag?.title} size={'xs'} {...(tag?.props || {})}>
                  {typeof tag === 'string' ? tag : tag?.label}
                </Tag>
              ))}
            </div>
          )}
        </div>
      )}
      {computedDescription && (
        <div className={'description-row'}>
          <div className={'description'}>{computedDescription}</div>
        </div>
      )}
      {bullets?.length > 0 && (
        <div className={'bullets-row'}>
          {displayBullets.map((bullet, index) => (
            <div key={index} className={'bullet'}>
              <IconV2 icon={GoTriangleRight} size={8} className={''} />
              {bullet}
            </div>
          ))}
        </div>
      )}
      {!computedTitle && tags?.length > 0 && (
        <div className={'tags-row'}>
          {tags.map((tag, index) => (
            <Tag key={index} title={typeof tag === 'string' ? null : tag?.title} size={'xs'} {...(tag?.props || {})}>
              {typeof tag === 'string' ? tag : tag?.label}
            </Tag>
          ))}
        </div>
      )}
      <div className={'pointer-chevron'}>
        {configured && onDelete && <Button className="ghost delete-feature-box-button" size="xs" title="Delete this configuration" icon={DeleteIcon} onClick={handleDelete} />}
        {pointerText && <div className={'pointer-text'}>{pointerText}</div>}
        <IconV2 icon={FiChevronRight} className={''} />
      </div>
    </div>
  );
};

export default PanelFeatureBox;
